import { type LucideIcon, X } from 'lucide-react'
import * as React from 'react'
import { cn } from '../../lib/cn'
import { Icon } from '../atoms/Icon'

/**
 * FilterPill — toggleable chip used in list filters. Pencil F1yZt / WKNVd.
 */
export interface FilterPillProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'> {
  active?: boolean
  icon?: LucideIcon
  onRemove?: () => void
  onClick?: () => void
  label?: React.ReactNode
}

export const FilterPill = React.forwardRef<HTMLButtonElement, FilterPillProps>(
  ({ className, active, icon, onClick, onRemove, children, label, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      onClick={onClick}
      aria-pressed={active || undefined}
      className={cn(
        'inline-flex items-center gap-1 h-8 px-3 rounded-pill border transition-colors text-body-sm',
        active
          ? 'bg-primary text-primary-foreground border-primary'
          : 'bg-transparent text-ia-text border-ia-border hover:bg-ia-surface-hover',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
        className,
      )}
      {...props}
    >
      {icon && <Icon icon={icon} size="xs" />}
      {label ?? children}
      {onRemove && active && (
        <span
          role="button"
          tabIndex={-1}
          onClick={(e) => {
            e.stopPropagation()
            onRemove()
          }}
          className="ml-1 -mr-1 opacity-80 hover:opacity-100"
          aria-label="Remove filter"
        >
          <X size={12} strokeWidth={2} />
        </span>
      )}
    </button>
  ),
)
FilterPill.displayName = 'FilterPill'
